/**
 * Every version of a playbook: its number, status, when and by whom it was
 * published, and why. A version opens read-only — its manifest and its
 * instructions as they were — so a change can be read against the one
 * before.
 */

import { useState } from 'react';

import { Badge, Button, Group, Loader, Modal, Stack, Table, Text } from '@mantine/core';

import * as Sentry from '@sentry/react';

import { MarkdownParser } from '@/components/Containers/MarkdownParser';
import { vnextApiClient } from '@/services/vnext-api';
import type { PlaybookVersion, PlaybookVersionSummary } from '@/types/playbook';
import { toError } from '@/utils/errors';
import { formatWhen } from './format';

interface VersionHistoryProps {
  playbookKey: string;
  versions: PlaybookVersionSummary[];
}

export function VersionHistory({ playbookKey, versions }: Readonly<VersionHistoryProps>) {
  const [opened, setOpened] = useState<PlaybookVersion | null>(null);
  const [loading, setLoading] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = async (number: number) => {
    setLoading(number);
    setError(null);
    try {
      setOpened(await vnextApiClient.getPlaybookVersion(playbookKey, number));
    } catch (err) {
      Sentry.captureException(toError(err));
      setError(toError(err, "The version couldn't be read").message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <Stack gap="xs" data-testid="version-history">
      <Table highlightOnHover fz="sm">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Version</Table.Th>
            <Table.Th>Published</Table.Th>
            <Table.Th>Why</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {versions.map((version) => (
            <Table.Tr key={version.number} data-testid={`version-${version.number}`}>
              <Table.Td>
                <Group gap="xs">
                  <Text size="sm">v{version.number}</Text>
                  <Badge
                    size="xs"
                    variant={version.status === 'live' ? 'light' : 'outline'}
                    color={version.status === 'live' ? 'teal' : 'gray'}
                  >
                    {version.status}
                  </Badge>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{formatWhen(version.publishedAt ?? version.createdAt)}</Text>
                <Text size="xs" c="dimmed">
                  {version.publishedBy ?? version.createdBy ?? ''}
                </Text>
              </Table.Td>
              <Table.Td>
                <Text size="sm" lineClamp={2}>
                  {version.changeReason ?? '—'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  loading={loading === version.number}
                  onClick={() => void open(version.number)}
                  data-testid={`version-open-${version.number}`}
                >
                  Read
                </Button>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {error ? (
        <Text size="xs" c="red">
          {error}
        </Text>
      ) : null}
      <Modal
        opened={opened !== null}
        onClose={() => setOpened(null)}
        title={opened ? `Version ${opened.number}` : ''}
        size="lg"
      >
        {opened ? (
          <Stack gap="sm" data-testid="version-view">
            <Text size="xs" c="dimmed">
              {opened.status} · published {formatWhen(opened.publishedAt)}
              {opened.publishedBy ? ` by ${opened.publishedBy}` : ''}
              {opened.houseVersion ? ` · over house v${opened.houseVersion}` : ''}
              {opened.changeReason ? ` · “${opened.changeReason}”` : ''}
            </Text>
            <Text size="sm" fw={600}>
              Manifest
            </Text>
            <Text
              size="xs"
              component="pre"
              style={{ whiteSpace: 'pre-wrap', fontFamily: 'var(--mantine-font-family-monospace)' }}
            >
              {JSON.stringify(opened.manifest, null, 2)}
            </Text>
            <Text size="sm" fw={600}>
              Instructions
            </Text>
            <MarkdownParser content={opened.instructions} />
            <Text size="sm" fw={600}>
              Documents
            </Text>
            <Stack gap={2}>
              {opened.documents.map((d) => (
                <Text key={d.key} size="xs">
                  <strong>{d.key}</strong> · {d.kind} ·{' '}
                  {d.appliesTo === 'all' ? 'all' : d.appliesTo.join(', ')}
                  {d.description ? ` — ${d.description}` : ''}
                </Text>
              ))}
            </Stack>
          </Stack>
        ) : (
          <Loader size="sm" />
        )}
      </Modal>
    </Stack>
  );
}
